import { useMutation } from 'convex/react';
import { useState } from 'react';
import { api } from '@repo/backend';
import { toUserFriendlyErrorMessage } from '@/lib/errors';

export type SubmitCardListingPayload = {
  sellerName?: string;
  sellerEmail?: string;
  name: string;
  description: string;
  flavorText?: string;
  rarity: 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary';
  cardType: 'creature' | 'spell' | 'artifact' | 'enchantment' | 'instant' | 'land';
  manaCost?: number;
  power?: number;
  toughness?: number;
  imageUrl?: string;
  startingPrice: number;
  reservePrice?: number;
  startsAt: number;
  endsAt: number;
};

type SubmitCardListingState =
  | { status: 'idle' }
  | { status: 'pending' }
  | {
      status: 'success';
      auctionId: string;
      cardId: string;
      sellerId: string;
    }
  | { status: 'error'; message: string };

/**
 * Public listing submission hook for card + auction creation.
 */
export function useSubmitCardListing() {
  const mutate = useMutation(api.auctions.mutations.submitCardListing);
  const [state, setState] = useState<SubmitCardListingState>({ status: 'idle' });

  async function submitListing(payload: SubmitCardListingPayload) {
    setState({ status: 'pending' });

    try {
      const result = await mutate(payload);
      setState({ status: 'success', ...result });
      return result;
    } catch (error) {
      const message = toUserFriendlyErrorMessage(
        error,
        'We could not create the listing. Please review your details and try again.',
      );
      setState({ status: 'error', message });
      throw error;
    }
  }

  function reset() {
    setState({ status: 'idle' });
  }

  return { submitListing, reset, state };
}
